/*
 * CareVego product finder
 * Drives the 3-question quiz, scores the configured products against the
 * answers, renders recommendation cards, and wires each card's primary
 * button to Shopify's cart (/cart/add.js).
 */
(function () {
  const money = (s) => s;
  const clean = (arr) => (arr || []).map((v) => String(v).trim()).filter(Boolean);
  const DURATIONS = ['6', '12', '24', 'multi'];

  class CvFinder extends HTMLElement {
    connectedCallback() {
      try {
        this.products = JSON.parse(this.querySelector('[data-cv-products]').textContent) || [];
      } catch (e) {
        this.products = [];
      }
      const cfg = this.querySelector('[data-cv-config]');
      this.wa = {
        number: (cfg && cfg.dataset.waNumber) || '',
        message: (cfg && cfg.dataset.waMessage) || '',
        show: !cfg || cfg.dataset.showWa !== 'false',
      };
      this.answers = { q1: null, q2: null, q3: 2 };

      this.bindChips();
      this.bindSlider();
      this.bindProceed();
    }

    bindChips() {
      this.querySelectorAll('.cv-chip').forEach((chip) => {
        chip.addEventListener('click', () => {
          const q = chip.dataset.q;
          this.querySelectorAll('.cv-chip[data-q="' + q + '"]').forEach((c) => c.classList.remove('is-selected'));
          chip.classList.add('is-selected');
          this.answers[q] = chip.dataset.value;
          setTimeout(() => {
            if (q === 'q1') this.showStep(2);
            if (q === 'q2') this.showStep(3);
          }, 320);
        });
      });
    }

    bindSlider() {
      const slider = this.querySelector('[data-slider]');
      const display = this.querySelector('[data-slider-display]');
      if (!slider) return;
      slider.addEventListener('input', () => {
        const v = parseInt(slider.value, 10);
        this.answers.q3 = v;
        display.innerHTML = v + ' <span>' + (v === 1 ? 'injection / pen' : 'injections / pens') + '</span>';
      });
    }

    bindProceed() {
      const btn = this.querySelector('[data-proceed]');
      if (btn) btn.addEventListener('click', () => this.recommend());
    }

    showStep(step) {
      this.querySelectorAll('.cv-qcard').forEach((card) => {
        const isTarget = card.dataset.q === 'q' + step;
        card.hidden = !isTarget;
        card.classList.toggle('is-active', isTarget);
        if (isTarget) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      this.querySelectorAll('.cv-step').forEach((el) => {
        const n = parseInt(el.dataset.step, 10);
        el.classList.toggle('is-done', n < step);
        el.classList.toggle('is-active', n === step);
      });
    }

    score(p) {
      let s = 0;

      // Situation match (travel / day / outdoor / home)
      if (this.answers.q1 && clean(p.situations).includes(this.answers.q1)) s += 3;

      // Duration match, with partial credit for the neighbouring bucket so
      // the closest-fit product stands out instead of everything tying.
      if (this.answers.q2) {
        const want = DURATIONS.indexOf(this.answers.q2);
        let best = 99;
        clean(p.durations).forEach((d) => {
          const gi = DURATIONS.indexOf(d);
          if (gi >= 0) best = Math.min(best, Math.abs(gi - want));
        });
        if (best === 0) s += 3;
        else if (best === 1) s += 1.25;
      }

      // Capacity fit: reward the tightest bag that still holds their pens, and
      // penalise recommending one that's oversized (or too small) for the load.
      // This stops a 6–7 pen Jumbo being pushed at someone carrying one pen.
      const inj = this.answers.q3;
      const min = p.minInj || 1;
      const max = p.maxInj || 10;
      if (inj >= min && inj <= max) {
        s += 3;
        s += Math.max(0, 1 - (max - inj) * 0.18);
      } else if (inj > max) {
        s -= (inj - max) * 0.5;
      } else {
        s -= (min - inj) * 0.3;
      }

      return s;
    }

    recommend() {
      const ranked = this.products
        .map((p) => ({ p, s: this.score(p) }))
        // Best fit first; "featured" is only a tie-breaker now, never a thumb on the scale.
        .sort((a, b) => b.s - a.s || (b.p.featured ? 1 : 0) - (a.p.featured ? 1 : 0));

      // Show genuinely-suitable products; if fewer than 3 clear the bar, top up
      // with the next best so the grid still reads as a set of three.
      let picks = ranked.filter((r) => r.s > 0);
      if (picks.length < 3) picks = picks.concat(ranked.filter((r) => r.s <= 0));
      picks = picks.slice(0, 3);

      // The single best pick is highlighted as the match.
      const bestScore = picks.length ? picks[0].s : 0;

      const cards = picks
        .map((r, i) => this.renderCard(r.p, i === 0 && r.s === bestScore))
        .join('');

      const rec = this.querySelector('[data-rec]');
      this.querySelector('[data-rec-cards]').innerHTML = cards;

      // Contextual copy
      const { q1, q2, q3 } = this.answers;
      let title = 'Here are our top picks for your situation.';
      let sub = 'Based on your answers, any of these will suit you — pick by size preference.';
      if (q3 >= 5 || q2 === 'multi' || q2 === '24' || q1 === 'travel') {
        title = 'For your journey, more capacity is the right call.';
        sub = 'Long trips or bulk insulin — these keep things cool the longest.';
      } else if (q1 === 'day' || q2 === '6' || q3 <= 2) {
        title = 'A compact cooler is all you need.';
        sub = 'For daily carry and short trips, lighter is better.';
      }
      this.querySelector('[data-rec-title]').textContent = title;
      this.querySelector('[data-rec-sub]').textContent = sub;

      rec.hidden = false;
      this.querySelectorAll('[data-rec] .cv-reveal, [data-rec]').forEach((el) => el.classList.add('is-visible'));
      setTimeout(() => rec.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);

      this.querySelectorAll('.cv-step').forEach((el) => {
        el.classList.remove('is-active');
        el.classList.add('is-done');
      });
    }

    renderCard(p, isBest) {
      const esc = (s) => String(s == null ? '' : s).replace(/"/g, '&quot;');
      const specs = (p.specs || [])
        .map((sp) => '<div class="cv-spec"><span class="cv-spec__icon">' + (sp.icon || '•') + '</span> ' + sp.text + '</div>')
        .join('');
      let savePill = '';
      if (p.compareRaw && p.priceRaw && p.compareRaw > p.priceRaw) {
        const pct = Math.round(((p.compareRaw - p.priceRaw) / p.compareRaw) * 100);
        if (pct > 0) savePill = '<span class="cv-card__save">Save ' + pct + '%</span>';
      }
      const price = p.price
        ? '<div class="cv-card__price">' + money(p.price) + (p.comparePrice ? '<s>' + money(p.comparePrice) + '</s>' : '') + savePill + '</div>'
        : '';
      const waBtn = this.wa.show
        ? '<button type="button" class="cv-btn-wa" data-cv-chat-open="' +
          esc(this.wa.message + ' ' + p.title) +
          '">💬 Ask about this</button>'
        : '';

      // Real product → add-to-cart; demo card (no product) → optional fallback link.
      let primaryBtn = '';
      if (p.hasProduct) {
        primaryBtn = p.available
          ? '<button type="button" class="cv-btn-card" data-add data-variant="' + p.variantId + '">Add to cart</button>'
          : '<button type="button" class="cv-btn-card" disabled>Sold out</button>';
      } else if (p.url) {
        primaryBtn = '<a class="cv-btn-card" href="' + esc(p.url) + '">' + (p.fallbackLabel || 'Learn more') + '</a>';
      }

      const img = p.image
        ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">'
        : '<span class="cv-card__img-emoji">' + (p.emoji || '🧊') + '</span>';
      const imgTag = p.url ? 'a href="' + esc(p.url) + '"' : 'div';
      const imgClose = p.url ? 'a' : 'div';
      const nameTag = p.url ? 'a href="' + esc(p.url) + '"' : 'span';
      const nameClose = p.url ? 'a' : 'span';

      return (
        '<div class="cv-card' + (isBest ? ' is-featured' : '') + '">' +
        (isBest ? '<div class="cv-card__badge">Best Match</div>' : '') +
        '<' + imgTag + ' class="cv-card__img">' + img +
        (p.cooling ? '<span class="cv-card__cooling">⏱ ' + p.cooling + '</span>' : '') +
        '</' + imgClose + '>' +
        '<div class="cv-card__body">' +
        (p.model ? '<div class="cv-card__model">' + p.model + '</div>' : '') +
        '<' + nameTag + ' class="cv-serif cv-card__name" style="text-decoration:none;color:inherit;">' + p.title + '</' + nameClose + '>' +
        (p.tagline ? '<div class="cv-card__tagline">' + p.tagline + '</div>' : '') +
        (specs ? '<div class="cv-card__specs">' + specs + '</div>' : '') +
        price +
        '<div class="cv-card__actions">' + primaryBtn + waBtn + '</div>' +
        '</div></div>'
      );
    }

  }

  if (!customElements.get('cv-finder')) customElements.define('cv-finder', CvFinder);

  // Page-wide add-to-cart for any CareVego card button (finder + range),
  // guarded so it only binds once even if multiple cv- sections load this file.
  if (!window.__cvCartInit) {
    window.__cvCartInit = true;
    document.addEventListener('click', async (evt) => {
      const btn = evt.target.closest('[data-add][data-variant]');
      if (!btn || btn.disabled) return;
      evt.preventDefault();
      const id = Number(btn.dataset.variant);
      const original = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
        btn.textContent = 'Added ✓';
        window.location.href = '/cart';
      } catch (e) {
        btn.textContent = original;
        btn.disabled = false;
        alert('Sorry, could not add to cart. Please try the product page.');
      }
    });
  }

  // Shared scroll-reveal for all cv-reveal elements on the page.
  function initReveal() {
    const els = document.querySelectorAll('.cv-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    els.forEach((el) => io.observe(el));
  }

  if (document.readyState !== 'loading') initReveal();
  else document.addEventListener('DOMContentLoaded', initReveal);
})();
